import React from 'react';
import { marketingService } from '../../services/marketingService';
import type { PatientTrend } from '../../types/marketing';

interface PatientChartProps {
  trends: PatientTrend[];
  title: string;
}

const PatientChart: React.FC<PatientChartProps> = ({ trends, title }) => {
  if (!trends || trends.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  const maxPatients = Math.max(...trends.map(t => t.total_patients));
  const minPatients = 0; // Force min to 0 for proper bar anchoring

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      
      {/* Chart Area */}
      <div className="relative h-64 mb-4 ml-12 sm:ml-16 mt-4">
        <div className="absolute inset-0 flex items-end justify-between space-x-1 px-1">
          {trends.map((trend, index) => {
            const heightPct = maxPatients === 0 ? 0 : (trend.total_patients / maxPatients) * 100;
            const barHeight = Math.max(heightPct, trend.total_patients > 0 ? 4 : 0);
            
            const newHeight = trend.total_patients > 0 ? (trend.new_patients / trend.total_patients) * 100 : 0;
            const returningHeight = trend.total_patients > 0 ? (trend.returning_patients / trend.total_patients) * 100 : 0;
            
            return (
              <div key={index} className="flex-1 group relative" style={{ height: '100%' }}>
                {/* Stacked Bars - anchored at bottom */}
                <div 
                  className="absolute bottom-0 left-0 right-0 flex flex-col-reverse"
                  style={{ height: `${barHeight}%` }}
                >
                  {/* New Patients (bottom) */}
                  <div
                    className="w-full bg-green-500 hover:bg-green-600 transition-colors duration-200"
                    style={{ height: `${newHeight}%` }}
                  />
                  {/* Returning Patients (top) */}
                  <div
                    className="w-full bg-blue-500 hover:bg-blue-600 transition-colors duration-200 rounded-t"
                    style={{ height: `${returningHeight}%` }}
                  />
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 whitespace-nowrap">
                    <div>{marketingService.formatDateShort(trend.date)}</div>
                    <div>Total: {marketingService.formatNumber(trend.total_patients)}</div>
                    <div>New: {marketingService.formatNumber(trend.new_patients)}</div>
                    <div>Returning (Lama): {marketingService.formatNumber(trend.returning_patients)}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-t-4 border-t-gray-800 border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] sm:text-xs text-gray-500 -ml-12 sm:-ml-16 pr-2 text-right w-12 sm:w-16 break-words">
          <span className="truncate">{marketingService.formatNumber(maxPatients)}</span>
          <span className="truncate">{marketingService.formatNumber(Math.round((maxPatients + minPatients) / 2))}</span>
          <span className="truncate">{marketingService.formatNumber(minPatients)}</span>
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="flex justify-between text-[9px] sm:text-xs text-gray-500 ml-12 sm:ml-16 mt-2 overflow-hidden">
        {trends.map((trend, index) => {
          const shouldShow = trends.length <= 4 || index % Math.ceil(trends.length / 3) === 0 || index === trends.length - 1;
          if (!shouldShow) {
            return <span key={index} className="invisible w-0 sm:w-auto"></span>;
          }
          return (
            <span key={index} className="text-center truncate whitespace-nowrap px-1">
              {marketingService.formatDateShort(trend.date)}
            </span>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center justify-center gap-2 sm:gap-6 text-xs sm:text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-2 shrink-0"></div>
          <span className="text-gray-600 truncate">New Patients</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded mr-2 shrink-0"></div>
          <span className="text-gray-600 truncate">Returning Patients (Pasien Lama)</span>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Total</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatNumber(trends.reduce((sum, t) => sum + t.total_patients, 0))}
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">New</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatNumber(trends.reduce((sum, t) => sum + t.new_patients, 0))}
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Returning (Pasien Lama)</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatNumber(trends.reduce((sum, t) => sum + t.returning_patients, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientChart;